import ms, { type StringValue } from "ms";
import { z } from "zod";
import { defineStepConfig } from "../registry";
import { SystemEntity, SystemEvent } from "@erp/shared-types";
import { entityOfType } from "../utils/system-entities";

const inputSchema = z.never();

const isMsParseable = (val: string): boolean => {
  try {
    return ms(val as StringValue) != null;
  } catch {
    return false;
  }
};

const workflowExecutionTimeoutSchema = z
  .string()
  .optional()
  .refine((val) => val === undefined || isMsParseable(val), {
    message:
      "Must be a ms-parseable duration string (e.g., '2 hours', '30 minutes', '90s')",
  })
  .describe(
    "Max workflow execution time as ms-parseable string (e.g., '2 hours'). Defaults to 1 hour.",
  );

const configSchema = z.object({
  emailRuleIds: z
    .array(z.uuid())
    .min(1)
    .describe("Email rule IDs that trigger this workflow"),
  skipSelfSent: z
    .boolean()
    .default(true)
    .describe(
      "When true, do not trigger this workflow for outbound emails that loop back into our own inbox (i.e. emails the system sent that re-appeared as inbound). Does not affect replies from external parties. Defaults to true.",
    ),
  workflowExecutionTimeout: workflowExecutionTimeoutSchema,
});

export type EmailRuleTriggerConfig = z.infer<typeof configSchema>;

const outputSchema = z.object({
  inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
    "The inbound email that matched the rule",
  ),
  documents: z
    .array(entityOfType(SystemEntity.CORE_DOCUMENT))
    .optional()
    .describe("Documents (attachments + email body) associated with the email"),
  from: z.string().describe("Sender email address"),
  to: z.string().describe("Recipient email address(es)"),
  cc: z.string().optional().describe("CC email address(es)"),
  subject: z.string().optional().describe("Email subject line"),
  conversationId: z
    .string()
    .optional()
    .describe("Email conversation/thread ID"),
});

const emits = [] as const;

export const emailRuleTriggerStep = defineStepConfig({
  type: "email_rule_trigger",
  name: "Email Rule Trigger",
  description:
    "Entry point for workflows triggered by an inbound email matching an email rule",
  category: "Trigger",
  inputSchema,
  outputSchema,
  configSchema,
  emits,
});

/**
 * The system event type this trigger subscribes to internally.
 * Used by the orchestration layer to route EMAIL_ROUTED_EMAIL_RULE events
 * to workflows using either the legacy event_trigger or the new email_rule_trigger.
 */
export const EMAIL_RULE_TRIGGER_EVENT_TYPE =
  SystemEvent.EMAIL_ROUTED_EMAIL_RULE;
